
import { uploadFileV2, fileTextract } from 'api/actions/file';
import { toast } from 'components/Shared/Toast';
import { useChannel, useEvent } from '@harelpls/use-pusher';
import React, { useState, useEffect, Fragment, useRef } from 'react';
import { contractAction, fetchProperty } from 'api/actions/contracts';
import PdfPreview from 'components/Shared/PdfPreview';
import { useTranslation } from 'react-i18next';
import { isValidFileType, noop } from 'utils';
import { fetcher } from 'api/client';
import bootbox from 'components/Modals/Bootbox';
import { useForm } from 'react-hook-form';
import {
    Button, Input, Nav, NavItem, NavLink,
    FormGroup, Form, Label, CustomInput,
    Modal,
    ModalHeader,
    ModalBody,
} from 'reactstrap';
import clsx from 'clsx';
import { useMutation, useQuery, useQueryClient } from 'react-query';

const getRatio = (val, measure, unit = '') => val * measure + unit;

function Markup({ pdf, selectText, currentPage, width, height, blockType, t }) {
    if (!pdf || !pdf.documentAnalysis) return null;

    if (pdf.documentAnalysis.status === 'FAILED')
        return (
            <div>{t('uploadAnalysis.failed')}</div>
        )
    return (
        <Fragment>
            {
                pdf.documentAnalysis && pdf.documentAnalysis.Blocks?.map((block) => {
                    // pdf.documentAnalysis.Blocks && pdf.documentAnalysis.Blocks.map((block) => {
                    if (block.Page === currentPage) {
                        if (block.Text && block.BlockType === blockType && ['WORD', 'LINE'].includes(blockType))
                            return (
                                <div
                                    className="position-absolute border border-primary"
                                    style={{
                                        left: getRatio(block.Geometry.BoundingBox.Left, width, 'px'),
                                        top: getRatio(block.Geometry.BoundingBox.Top, height, 'px'),
                                        width: getRatio(block.Geometry.BoundingBox.Width, width, 'px'),
                                        height: getRatio(block.Geometry.BoundingBox.Height, height, 'px')
                                    }}
                                    onClick={() => selectText(block.Text)}>
                                </div>
                            )
                        else if (blockType === 'KEY_VALUE_SET' && block?.EntityTypes?.[0]) {
                            return (
                                <div
                                    className="position-absolute border border-primary"
                                    style={{
                                        left: getRatio(block.Geometry.BoundingBox.Left, width, 'px'),
                                        top: getRatio(block.Geometry.BoundingBox.Top, height, 'px'),
                                        width: getRatio(block.Geometry.BoundingBox.Width, width, 'px'),
                                        height: getRatio(block.Geometry.BoundingBox.Height, height, 'px'),
                                        borderColor: block.EntityTypes[0] === 'KEY' ? 'purple' : 'green'
                                    }}
                                    onClick={() => selectText(block.Text, block.Relationships, pdf.documentAnalysis)}>
                                </div>
                            )
                        }
                    }
                })
            }
        </Fragment>
    )
}

function Vars({ participants, setselectedInput, saveVars, t, selectedInput }) {

    const { register, handleSubmit, reset } = useForm({});

    return (
        <Form onSubmit={handleSubmit(saveVars)}>
            <FormGroup>
                {
                    participants.map((p) => (p?.variables && p?.variables.map((v) => (
                        <Fragment>
                            <Label htmlFor="exampleEmail" className="mt-2">{v.name}</Label>
                            <Input
                                className={clsx(selectedInput === v.apiName ? 'input-shadowed' : '')}
                                id={v.apiName}
                                name={v.apiName}
                                type={v.type === 'text' ? 'textarea' : v.type}
                                innerRef={register()}
                                placeholder={v.name}
                                defaultValue={v.value}
                                onFocus={() => setselectedInput(v.apiName)}
                            />
                        </Fragment>
                    ))))
                }
            </FormGroup>
            <Button color="primary" type="submit" >{t('common.save')}</Button>
            <Button color="default" className="ml-2" onClick={reset} >{t('common.reset')}</Button>
        </Form>
    )
}

export default function ModalUpload({ contract, onClose, onSuccess, isOpen }) {

    const { t } = useTranslation();

    const { _id: contractId, participants = [] } = contract ?? {};

    const hasVars = participants.some(p => p?.variables && p?.variables.length);

    const width = 595;
    const [hasChanged, setHasChanged] = useState(false);
    const [selectedInput, setselectedInput] = useState('');
    const [currentPage, setcurrentPage] = useState(1);
    const [concatModeActive, setconcatModeActive] = useState(false);
    const [activeTab, setActiveTab] = useState('1');
    const [pageHeight, setPageHeight] = useState(0);
    const [blockType, setblockType] = useState('LINE');
    const [pdf, setpdf] = useState();
    const queryClient = useQueryClient();


    const inputFileRef = useRef(null);

    const goTo = (page) => setcurrentPage(page);

    const channel = useChannel(contractId ? `private-${contractId}` : null);

    useEvent(channel, 'fileAnalysed', () => {
        toast.success(t('uploadAnalysis.fileAnalysed'));
        queryClient.invalidateQueries('fetch-files');
    });


    const { data: stateFiles = [] } = useQuery('fetch-files', () => fetcher(fetchProperty(contractId, 'files')));

    const { mutate: uploadMutation, isLoading } = useMutation((payload) => fetcher(uploadFileV2(payload, noop, { 'X-Convert-TO-PDF': true })), {
        onSuccess: ({ fileId }) => {
            addToContractMutation({ fileId })
        }
    });
    const { mutate: addToContractMutation } = useMutation((payload) => fetcher(contractAction(contractId, 'addfile', payload)), {
        onSuccess: (result, { fileId }) => {
            extractTextMutation({ fileId })
        }
    });
    const { mutate: extractTextMutation, data } = useMutation((payload) => fetcher(fileTextract(payload)), {
        onSuccess: () => {

        }
    });

    const { mutate: removeFileMutation } = useMutation((payload) => fetcher(contractAction(contractId, 'removefile', payload)), {
        onSuccess: () => {
            toast.success(t('success'));
            setpdf();
            queryClient.invalidateQueries('fetch-files');
        }
    });

    const { mutate: saveVarsMutation } = useMutation((payload) => fetcher(contractAction(contractId, 'complete', payload)), {
        onSuccess: (data) => {
            onSuccess(data)
            toast.success(t('success'));
            setHasChanged(true);
        }
    });

    const handleChange = async (eFile) => {
        const file = eFile.target.files[0];
        if (!isValidFileType({ filename: file.name, acceptedExtensions: ['png', 'jpg', 'pdf', 'jpeg'] })) return toast.error(t('errors.NotSupportedFileType'));
        uploadMutation(file);
    }

    const handleDelete = async (fileId) => {
        if (!await bootbox.confirmPromise(t('uploadAnalysis.confirm_remove'))) return;
        removeFileMutation({ fileId })
    }

    const viewFile = (url, documentAnalysis) => {
        setpdf({ url, documentAnalysis })
        setcurrentPage(1);
    }

    const handlePageLoad = ({ height }) => {
        setPageHeight(height)
    }

    const saveVars = (vars) => {
        saveVarsMutation(vars);
    }

    const selectText = (text, relationships, documentAnalysis) => {
        let foundText = null;
        if (documentAnalysis && relationships?.[0] && documentAnalysis.Blocks) {
            const childs = documentAnalysis.Blocks.filter((b) => relationships[0].Ids.includes(b.Id));
            foundText = childs.map(x => x.Text).join(' ');
        }
        const found = document.getElementById(selectedInput);
        const newText = foundText || text;
        if (!newText) return;
        if (concatModeActive) found.value += newText + ' ';
        else found.value = newText;
    }

    return (
        <Modal isOpen={isOpen} centered className="modal-lg">
            <ModalHeader toggle={onClose}>
                {t('uploadAnalysis.information')}
            </ModalHeader>
            <ModalBody>
                <div className="reference-body d-flex" style={{ overflow: 'scroll', top: 40, minHeight: 'calc(600px)' }}>
                    <div className={clsx(!!pdf ? '' : 'page-box-shadow mb-3 ')} style={{ backgroundColor: !!pdf ? '#fff' : '#e0e0e0' }}>
                        {
                            pdf ?
                                <PdfPreview
                                    url={pdf.url}
                                    pageNumber={currentPage}
                                    width={width}
                                    onPageChange={goTo}
                                    controls
                                    onPageLoad={handlePageLoad}
                                >
                                    <Markup
                                        pdf={pdf}
                                        selectText={selectText}
                                        currentPage={currentPage}
                                        width={width}
                                        height={pageHeight}
                                        blockType={blockType}
                                        t={t}
                                    />
                                </PdfPreview>
                                :
                                <div style={{ width }} className=""></div>
                        }
                    </div>
                    <div className="px-5" style={{ width: `calc(100% - ${width}px)` }}>
                        <div className="mb-3">
                            <h4 className="font-bold">{t('common.files')}</h4>
                        </div>
                        <div className="d-flex mb-4">
                            {
                                stateFiles.map((f, i) => {
                                    const Job = f?.Jobs[0] || {};
                                    return (
                                        <div className="pointer mr-2"
                                            style={{ position: 'relative' }}
                                            onClick={() => { viewFile(f.url, Job.documentAnalysis) }}>
                                            <img src="/images/pdf.png" id={`pdf-image-${i}`} title={f.name} alt={f.name} />
                                            {Job.completed ?
                                                <i className="fas fa-check text-success"
                                                    style={{ position: 'absolute', float: 'left', left: '70px', top: '5px' }}></i>
                                                :
                                                <i className="fas fa-spin fa-spinner"
                                                    style={{ position: 'absolute', float: 'left', left: '70px', top: '5px' }}></i>
                                            }
                                            <i className="fas fa-times text-danger" onClick={() => handleDelete(f._id)}
                                                style={{ position: 'absolute', float: 'left', left: '10px', top: '5px' }}></i>
                                        </div>
                                    )
                                })
                            }
                            <div className="pointer" style={{ position: 'relative' }} onClick={() => { inputFileRef.current.click(); }}>
                                <img src="/images/pdf_plus.png" alt={'Add'} />
                                {isLoading &&
                                    <i className="fas fa-spin fa-spinner"
                                        style={{ position: 'absolute', float: 'left', left: '70px', top: '5px' }}></i>
                                }
                                <input type="file" onChange={handleChange} className="d-none" ref={inputFileRef} accept="image/png,image/jpg,image/jpeg,application/pdf" />
                            </div>
                        </div>
                        <FormGroup className={clsx('mt-2 mb-3', !!pdf || 'd-none')}>
                            <Label htmlFor="exampleCheckbox">{t('uploadAnalysis.selection_type')}</Label>
                            <div>
                                <CustomInput checked={blockType === 'LINE'} onClick={() => setblockType('LINE')} type="radio" id="CustomRadio" name="customRadio" label="Modo Línea" />
                                <CustomInput checked={blockType === 'WORD'} onClick={() => setblockType('WORD')} type="radio" id="CustomRadio2" name="customRadio" label="Modo Palabra" />
                                <CustomInput checked={blockType === 'KEY_VALUE_SET'} onClick={() => setblockType('KEY_VALUE_SET')} type="radio" id="CustomRadio4" name="customRadio" label="Modo Tabla" />
                            </div>
                            <Label check className="mt-1">
                                <Input type="checkbox" checked={concatModeActive} onClick={() => { setconcatModeActive(!concatModeActive) }} />{' '}{t('uploadAnalysis.multiple_selection')}
                            </Label>
                        </FormGroup>

                        {hasVars ?
                            <>
                                <Nav tabs>
                                    <NavItem>
                                        <NavLink
                                            tag="a"
                                            className={clsx('text-dark font-weight-thin pointer', activeTab === '1' && 'active', activeTab !== '1' && 'bg-grey border')}
                                        // onClick={() => { toggleTabs('1'); }}
                                        >
                                            {t('common.variables')}
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                                <Vars participants={participants} setselectedInput={setselectedInput} saveVars={saveVars} t={t} selectedInput={selectedInput} />
                            </> :
                            <span>{t('uploadAnalysis.no_vars')}</span>
                        }
                    </div>
                </div>
            </ModalBody>
        </Modal>
    )
}
